import { useAuth } from '../contexts/AuthContext'

// Roles that always hold every permission and are never scoped to specific apps
// — mirrors the backend (owner/admin bypass require_permission; owner/admin/
// superuser bypass user_scoped_to_app).
const FULL_ACCESS_ROLES = new Set(['owner', 'admin'])

/**
 * Per-capability + per-app UI gating, sourced from /auth/me (role permissions +
 * app scope). Use it to hide/disable actions the current user can't perform:
 *
 *   const { can, inApp } = usePermissions()
 *   {can('team:invite') && <InviteButton />}
 *   {inApp('signage') && <ScreensNavItem />}
 *
 * The backend stays authoritative — this only shapes the UI. Permission strings
 * match the backend Permission enum values (e.g. "screens:edit", "team:invite").
 */
export function usePermissions() {
  const { user } = useAuth()
  const role = user?.role ?? ''
  const isSuperuser = !!user?.is_superuser
  const fullAccess = isSuperuser || FULL_ACCESS_ROLES.has(role)
  const permissions = new Set(user?.permissions ?? [])
  const appScope = user?.app_scope ?? []

  /** Whether the user holds a capability (full-access roles bypass). */
  const can = (permission: string): boolean => fullAccess || permissions.has(permission)

  /** Whether the user is scoped to an app. Empty scope = no product-app
   *  access at all (confined to My) — only full-access roles bypass. */
  const inApp = (appKind: string): boolean =>
    fullAccess || appScope.includes(appKind)

  return { can, inApp, role, isSuperuser, fullAccess, permissions, appScope }
}
